import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import ScreenLoader from '../ScreenLoader/ScreenLoader'

const Table = styled.table`
  width: 100%;
  color: var(--color-text-primary);
  font-size: 12px;
  text-align: left;
  border: 1px solid var(--color-bg-sidebar);
  border-spacing: 0;
  background-color: var(--color-bg-card);
`

const Header = styled.tr`
  background-color: var(--color-bg-sidebar);
  color: var(--color-text-primary);
`

const Head = styled.th`
  min-height: 45px;
  padding: 10px;
  text-transform: capitalize;
`

const User = styled.tr`
  ${p => p.isAdmin && 'cursor: pointer'};
  transition: background-color 0.2s ease;

  &:nth-child(even) {
    background-color: rgba(42, 42, 42, 0.5);
  }

  &:hover {
    background-color: var(--color-bg-sidebar);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
  }
`

const Col = styled.td`
  height: 45px;
  padding: 10px;
  border-bottom: 1px solid var(--color-bg-sidebar);

  &:last-child {
    width: 70px;
  }
`

const Role = styled.span`
  display: inline-block;
  width: 70px;
  text-align: center;
  background-color: ${p =>
    p.role === 'admin'
      ? 'var(--color-primary)'
      : p.role === 'disabled'
        ? 'var(--color-text-disabled)'
        : 'var(--color-secondary)'};
  border-radius: 20px;
  color: var(--color-bg-main);
  font-size: 10px;
  font-weight: bold;
  padding: 6px 0;
  text-transform: uppercase;
`

const Fallback = styled.p`
  font-size: 14px;
  color: var(--color-text-secondary);
  text-align: center;
  margin-top: 22px;
`

const Tag = styled.button`
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
  line-height: 1;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s ease;
  border-radius: 20px;
  background-color: var(--color-primary);
  padding: 4px 8px;
  color: var(--color-bg-main);
  display: inline-flex;
  align-items: center;
  margin-left: 10px;

  img {
    margin-right: 5px;
  }

  &:hover {
    background-color: var(--color-secondary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-secondary);
    outline-offset: 2px;
  }
`

const UsersTable = ({ users = [], loading, error }) => {
  const history = useHistory()
  const { isSuperAdmin, isOrgAdmin } = useAuth()

  if (loading) return <ScreenLoader />
  if (error) return <p style={{ color: 'var(--color-error)' }} role="alert">{error.message}</p>

  return (
    <>
      <Table>
        <thead>
          <Header>
            <Head>Name</Head>
            <Head>Email</Head>
            <Head>Roles</Head>
          </Header>
        </thead>
        <tbody>
          {users.map(({ roles, user }) => {
            const onImpersonatedLink = e => {
              e.stopPropagation()
              const prettyId = user.id.split(':')[1]
              const authId = encodeURI(`auth0|${prettyId}`)
              const encoded = btoa(authId)
              const baseUrl = `https://manage.auth0.com/dashboard/us/trek10/users/${encoded}`
              window.open(baseUrl, '_blank')
            }

            const isClickable = isSuperAdmin || isOrgAdmin
            const handleRowClick = () => history.push(`/users/${user.id}`)
            const handleKeyDown = (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleRowClick()
              }
            }

            return (
              <User
                key={user.id}
                isAdmin={isClickable}
                {...isClickable && {
                  onClick: handleRowClick,
                  onKeyDown: handleKeyDown,
                  tabIndex: 0,
                  role: 'button',
                  'aria-label': `Edit ${user.firstName} ${user.lastName}`,
                }}
              >
                <Col>
                  {user.firstName} {user.lastName}
                  {isSuperAdmin && (
                    <Tag type="button" onClick={onImpersonatedLink} aria-label={`View ${user.firstName} ${user.lastName} in Auth0`}>
                      <span>SuperAdmin View As</span>
                    </Tag>
                  )}
                </Col>
                <Col>{user.email}</Col>
                <Col>
                  {user.disabled ? (
                    <Role role={'disabled'}>Disabled</Role>
                  ) : roles.includes('ADMIN') ? (
                    <Role role='admin'>Admin</Role>
                  ) : (
                    <Role role='user'>User</Role>
                  )}
                </Col>
              </User>
            )
          })}
        </tbody>
      </Table>
      {users.length < 1 && <Fallback role="status" aria-live="polite">No users found!</Fallback>}
    </>
  )
}

export default UsersTable
